import { FieldValues } from "react-hook-form";

import { analyticsService } from "../../services/analytics";
import { convertNestedEmptyToNull, createCampaignUpdate, generateNewDocId } from "../../services/firebase";
import { asIdList } from "../../shared/asIdList";
import { emptyBrowseFilter } from "../../utils/browseFilter";
import { asPlatforms } from "../../shared/platforms";
import { BrowseFilter, Talent, User, Booking, Success, Failure, Campaign, List, SelectedTalent, Currency, Platform, LeadSource } from "../../shared/types";
import { createdByRoleFromUser } from "../../utils/campaignPermissions";
import { removeParamsFromURL } from "../../utils/core";
import { getCurrentMonthDue } from "../../utils/dates";

export type Step = "list" | "form" | "confirmation";

export type NewBookingState = {
    booker?: User,
    campaigns: Campaign[],
    filter: BrowseFilter,
    platforms: Platform[],
    selectedTalent: SelectedTalent,
    totalTiktokFollowers: number,
    totalInstagramFollowers: number,
    totalPrice: number,
    step: Step,
    submissionStatus?: Success | Failure,
    showBrandAlert: boolean,
    creedModalOpen: boolean,
    creedModalShown: boolean,
    showMobileSummary: boolean,
}

export type NewBookingStateAction =
    { type: "setStatePartial", payload: Partial<NewBookingState> } |
    { type: "navigateToStep", step: Step } |
    { type: "updateFilter", filterPartial: Partial<BrowseFilter> } |
    { type: "setBooker", booker: User | undefined } |
    { type: "setSubmissionStatus", status: Success | Failure };

interface StateOptions {
    user?: User | undefined,
    listId?: string,
}

export function getInitialState(stateOptions: StateOptions): NewBookingState {
    const { user, listId } = stateOptions;
    const booker = user?.roles?.booker && !user?.roles?.manager ? user : undefined;
    const lockTalentCards = !booker;

    const filter = {
        ...emptyBrowseFilter,
        ...getBookerFilterOptions(booker),
        listId: listId || (user?.roles?.manager ? "my-roster" : ""),
        lockTalentCards
    };

    return {
        booker: booker,
        campaigns: [],
        filter: filter,
        platforms: [],
        selectedTalent: {},
        totalTiktokFollowers: 0,
        totalInstagramFollowers: 0,
        totalPrice: 0,
        step: "list",
        submissionStatus: undefined,
        showBrandAlert: true,
        creedModalOpen: false,
        creedModalShown: false,
        showMobileSummary: false,
    } as NewBookingState;
}

export function newBookingStateReducer(state: NewBookingState, action: NewBookingStateAction): NewBookingState {
    switch (action.type) {
        case "setStatePartial": {
            return { ...state, ...action.payload } as NewBookingState;
        }
        case "navigateToStep": {
            const step = action.step;
            analyticsService().logEvent(step === "list" ? "Viewed Booking Talent List" : "Viewed Booking Form");
            return { ...state, step } as NewBookingState;
        }
        case "setBooker": {
            const { booker } = action;

            const filter = {
                ...state.filter,
                ...getBookerFilterOptions(booker, state.filter.lockCurrencyFromList ? state.filter.currency : undefined),
                lockTalentCards: !booker
            };

            let creedModalOpen = false;
            if (booker?.clientId === "QgqnbwvLTq8GF3cQX7i5") {
                creedModalOpen = true;
            }

            return { ...state, booker, filter, creedModalOpen };
        }
        case "setSubmissionStatus": {
            const submissionStatus = action.status;
            return { ...state, submissionStatus };
        }
        case "updateFilter": {
            return {
                ...state,
                filter: {
                    ...state.filter,
                    ...action.filterPartial
                } as BrowseFilter
            };
        }
    }
}

export function calculateTotals(selectedTalent: SelectedTalent, talent: Talent[]) {
    let totalPrice = 0;
    let totalTiktokFollowers = 0;
    let totalInstagramFollowers = 0;

    for (const selectedTal of Object.values(selectedTalent)) {
        totalPrice += selectedTal.price || 0;
    }

    for (const selectedTalId of Object.keys(selectedTalent)) {
        const curTal = talent.filter((tal: Talent) => tal.id === selectedTalId)[0];
        if (curTal?.platformData?.TikTok?.followers) totalTiktokFollowers += curTal.platformData.TikTok.followers;
        if (curTal?.platformData?.Instagram?.followers) totalInstagramFollowers += curTal.platformData.Instagram.followers;
    }

    return {
        totalPrice,
        totalTiktokFollowers,
        totalInstagramFollowers,
    };
}

export function getBookerFilterOptions(booker: User | undefined, listCurrency?: Currency) {
    const filter: Partial<BrowseFilter> = {};

    if (listCurrency) {
        filter.currency = listCurrency;
        filter.lockCurrencyFromList = true;

        if (booker?.clientWideSettings?.currency && (booker?.clientWideSettings?.currency !== listCurrency)) {
            alert(`List currency is locked to ${listCurrency}. Ignoring booker currency of ${booker.clientWideSettings.currency}.`);
        }
    } else if (booker?.clientWideSettings?.currency) {
        filter.currency = booker.clientWideSettings.currency;
        filter.lockCurrencyFromBooker = true;
    } else {
        filter.lockCurrencyFromBooker = false;
        filter.lockCurrencyFromList = false;
        filter.currency = booker?.currency || "USD";
    }

    if (booker?.campaignType === "Song") {
        filter.campaignType = "Song";
        filter.showCampaignTypeSelect = false;
    } else if (booker?.campaignType === "Brand") {
        filter.campaignType = "Brand";
        filter.showCampaignTypeSelect = false;
    } else if (booker?.campaignType === "Multi") {
        filter.showCampaignTypeSelect = true;
    } else {
        filter.campaignType = "Song";
        filter.showCampaignTypeSelect = false;
    }

    return filter;
}

export function listLocksCampaignPlatform(list: List | undefined) {
    return Boolean(list?.id && list.id !== "my-roster" && asPlatforms(list.platform).length);
}

export function canContinueToBookingForm(args: { booker?: User; selectedTalent: SelectedTalent; platforms: Platform[] }) {
    return Boolean(args.booker && Object.keys(args.selectedTalent).length && args.platforms.length);
}

function formString(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined;
}

/** New Booking submit — campaign from the form, one booking per selected talent. */
export async function buildCampaignAndBookings(formData: FieldValues, authenticatedUser: User | undefined, state: NewBookingState, talent: Talent[], salesTeam: User[]) {
    const { booker, totalPrice, selectedTalent, platforms, filter: { campaignType, currency } } = state;

    let sendCampaignReceipt = true;
    let sendBookingOpp = true;

    if (authenticatedUser?.roles?.manager) {
        sendCampaignReceipt = false;
        sendBookingOpp = false;
    }
    if (authenticatedUser?.roles?.brandTeam) {
        sendCampaignReceipt = formData.sendCampaignReceipt === "true" ? true : false;
        sendBookingOpp = formData.sendBookingOpp === "true" ? true : false;
    }

    const campaignField: unknown = formData.campaign;
    let existingCampaignId: string | undefined;
    let campaignName: string | undefined;
    if (campaignField !== null && typeof campaignField === "object") {
        if ("id" in campaignField && typeof campaignField.id === "string" && campaignField.id) {
            existingCampaignId = campaignField.id;
        }
        if ("label" in campaignField && typeof campaignField.label === "string") {
            campaignName = campaignField.label;
        }
    }
    const campaignId = existingCampaignId ?? generateNewDocId("campaigns");
    const isNewCampaign = existingCampaignId === undefined;
    const salesTeamRaw: unknown = formData.salesTeamId;
    const salesTeamId = asIdList(
        typeof salesTeamRaw === "string"
            ? salesTeamRaw
            : Array.isArray(salesTeamRaw)
                ? salesTeamRaw.filter((item): item is string => typeof item === "string")
                : undefined,
    );
    const airtableSalesTeamId = salesTeamId.flatMap((id) => {
        const airtableId = salesTeam.find((teamMember) => teamMember.id === id)?.managerAirtableId;
        return airtableId ? [airtableId] : [];
    });
    const details = formString(formData.details) ?? "";
    const rawSoundUrl = formString(formData.soundUrl);
    const soundUrl = rawSoundUrl?.length ? removeParamsFromURL(rawSoundUrl) : null;
    const crmLeadId = formString(formData.crmLeadId);
    const leadSourceRaw: unknown = formData.leadSource;
    const leadSource = Array.isArray(leadSourceRaw)
        ? leadSourceRaw.filter((item): item is LeadSource =>
            item === "Inbound"
            || item === "Outreach"
            || item === "Sales Team"
            || item === "Grail Manager"
            || item === "Platform Booker"
            || item === "CRM Lead"
            || item === "TikTok Shop Referral")
        : [];
    const firstName = formString(formData.firstName);
    const lastName = formString(formData.lastName);
    const email = formString(formData.email);
    const company = formString(formData.company);
    const phone = formString(formData.phone);
    const boostInterest = formData.boostInterest === true || formData.boostInterest === "true";
    const isTest = email === "timothy.noetzel@gmail.com" || import.meta.env.DEV;

    const theCampaign = createCampaignUpdate({
        id: campaignId,
        ...convertNestedEmptyToNull({
            ...(isNewCampaign ? { airtableId: null } : {}),
            booker: {
                ...(booker?.airtableBookerId ? { airtableBookerId: booker.airtableBookerId } : {}),
                firstName,
                lastName,
                email,
                company,
                phone,
                uid: booker?.id,
            },
            ...(boostInterest ? { boostInterest: true } : {}),
            campaignName,
            campaignType,
            ...(platforms.length ? { campaignPlatform: platforms } : {}),
            ...(formData.crmLeadId !== undefined
                ? { crmLeadId: crmLeadId || null }
                : isNewCampaign ? { crmLeadId: null } : {}),
            lastUpdateByManager: authenticatedUser?.roles?.manager ? true : false,
            currency,
            details,
            ...(formData.leadSource !== undefined
                ? { leadSource: leadSource.length ? leadSource : null }
                : isNewCampaign ? { leadSource: null } : {}),
            originalQuote: totalPrice,
            ...(formData.salesTeamId !== undefined ? { salesTeamId, airtableSalesTeamId } : {}),
            sendBookingOpp,
            sendCampaignReceipt,
            soundUrl,
            uid: booker?.id,
            ...(isNewCampaign && authenticatedUser?.id
                ? {
                    createdById: authenticatedUser.id,
                    createdByRole: createdByRoleFromUser(authenticatedUser),
                }
                : {}),
            ...(isTest ? { isTest: true } : {}),
        }),
    });

    const newBookings = talent.filter((tal) => selectedTalent[tal.id]).map((tal): Booking => {
        return {
            airtableId: null,
            airtableCampaignId: null,
            talentId: tal.id,
            price: selectedTalent[tal.id].price,
            talentName: tal.platformData?.TikTok?.username || tal.platformData?.Instagram?.username,
            managerIds: tal.managerIds || [],
            airtableClientId: booker?.airtableClientId || "",
            ...(tal.airtableManagerIds && { airtableManagerIds: tal.airtableManagerIds }),
            id: generateNewDocId("bookings"),
            bookerId: booker?.id,
            ...(booker?.airtableBookerId && { airtableBookerId: booker?.airtableBookerId }),
            campaignId: theCampaign.id,
            campaignName,
            campaignType,
            ...(platforms.length ? { campaignPlatform: platforms } : {}),
            ...(booker?.clientId && { clientId: booker.clientId }),
            ...(booker?.company && { clientName: booker.company }),
            crmLeadId: formData.crmLeadId || null,
            leadSource: formData.leadSource?.length ? leadSource : null,
            placedByEmail: authenticatedUser?.email,
            placedById: authenticatedUser?.id,
            placedByRoles: Object.entries(authenticatedUser?.roles || {}).filter(([key, value]) => !!value).map(([key]) => key as keyof User["roles"]),
            placedByCreatorsManager: tal.managerIds?.includes(authenticatedUser?.id || "") ? true : false,
            createdByManager: authenticatedUser?.roles?.manager ? true : false,
            lastUpdateByManager: authenticatedUser?.roles?.manager ? true : false,
            currency,
            ...((formData.email === "timothy.noetzel@gmail.com" || import.meta.env.DEV) && { isTest: true }),
            monthDue2: getCurrentMonthDue(),
            ...(salesTeamId.length > 0 && { salesLeadId: salesTeamId, airtableSalesLeadId: airtableSalesTeamId }),
        }
    });

    return {
        campaign: theCampaign,
        bookings: newBookings
    };
}
